import { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'sonner'
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useLanguage } from '@/context/LanguageContext'
import {
  GET_PAGE_TAGS,
  UPSERT_PAGE_TAG,
} from '@/containers/PageTags/pageTagQueries'
import { PageTag, getTagName } from '@/containers/PageTags/types'

interface PageTagSelectorProps {
  value: number[]
  onChange: (ids: number[]) => void
}

export default function PageTagSelector({
  value,
  onChange,
}: PageTagSelectorProps) {
  const { language: currentLanguage } = useLanguage()
  const [open, setOpen] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [creating, setCreating] = useState(false)

  const { data, loading } = useQuery(GET_PAGE_TAGS, {
    fetchPolicy: 'cache-and-network',
  })

  const [upsertPageTag] = useMutation(UPSERT_PAGE_TAG, {
    refetchQueries: ['GET_PAGE_TAGS'],
  })

  const tags: PageTag[] = data?.findManyPageTag ?? []

  const selectedTags = value
    .map(id => tags.find(t => t.id === id))
    .filter((t): t is PageTag => Boolean(t))

  const availableTags = tags.filter(t => !value.includes(t.id))

  const handleRemove = (tagId: number) => {
    onChange(value.filter(id => id !== tagId))
  }

  const handleAdd = (tagId: number) => {
    if (!value.includes(tagId)) {
      onChange([...value, tagId])
    }
    setOpen(false)
  }

  const handleCreate = async () => {
    const name = newTagName.trim()
    if (!name || creating) return
    setCreating(true)
    try {
      const response = await upsertPageTag({
        variables: {
          page_tag_profiles: [{ language: currentLanguage, name }],
        },
      })
      const newId = response.data?.upsertPageTag?.id
      if (newId) {
        onChange([...value, newId])
      }
      setNewTagName('')
      toast.success('Tag created')
    } catch {
      toast.error('Failed to create tag')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="space-y-2">
      {/* Selected tag chips */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedTags.map(tag => (
            <Badge key={tag.id} variant="secondary" className="gap-1">
              <span className="truncate">{getTagName(tag, currentLanguage)}</span>
              <button
                type="button"
                onClick={() => handleRemove(tag.id)}
                className="ml-0.5 inline-flex items-center justify-center"
              >
                <X className="h-3 w-3 opacity-60 hover:opacity-100" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Add existing tag combobox */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">Add existing tag</span>
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="Search tags..." />
            <CommandList>
              <CommandEmpty>
                {loading ? 'Loading...' : 'No tags found.'}
              </CommandEmpty>
              <CommandGroup>
                {availableTags.map(tag => {
                  const label = getTagName(tag, currentLanguage)
                  return (
                    <CommandItem
                      key={tag.id}
                      value={`${tag.id}-${label}`}
                      onSelect={() => handleAdd(tag.id)}
                    >
                      <Check className="mr-2 h-4 w-4 shrink-0 opacity-0" />
                      <span className="truncate">{label}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Inline create new tag */}
      <div className="flex gap-2">
        <Input
          placeholder="New tag name"
          value={newTagName}
          onChange={e => setNewTagName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleCreate()
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={handleCreate}
          disabled={creating || !newTagName.trim()}
          className="shrink-0"
        >
          <Plus className="h-4 w-4 mr-1" />
          Create
        </Button>
      </div>
    </div>
  )
}
